<template>
    <!-- Admin → Inventario → Oferta disponible (/admin/oferta-disponible).
         Datos: GET admin/oferta-disponible (OfertaDisponibleService, guardado
         hasta 3 h; "Actualizar" lo recalcula con ?refrescar=1).
         Las vistas son las de la demo aprobada (odVistas.js / odPais.js): arman
         HTML con los textos ya escapados y acá se conectan los eventos por
         delegación (data-*). El estado vive en la URL:
           /admin/oferta-disponible               inicio
           /admin/oferta-disponible/c/europa      continente
           /admin/oferta-disponible/p/ES          país
           ?m=0..6|t  ci  cat  tr  es  gr  q  o  tab  n   (mes y filtros) -->
    <div class="od">
        <div class="od-main" ref="main" tabindex="-1">
            <div class="od-top">
                <nav class="od-migas" aria-label="Ruta" @click="onLink" v-html="migas"></nav>
                <p v-if="generado" class="od-fuente">
                    <span>Datos al {{ generadoTxt }}</span>
                    <button type="button" class="od-btn" :disabled="actualizando" @click="cargar(true)">
                        <svg :class="{ 'od-girar': actualizando }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-2.6-6.4M21 4v5h-5"/></svg>
                        {{ actualizando ? 'Calculando…' : 'Actualizar' }}
                    </button>
                    <span v-if="falloActualizar" class="od-fallo" role="alert">No se pudo actualizar</span>
                </p>
            </div>

            <div v-if="error" class="od-estado" role="alert">
                <h2>{{ error === 403 ? 'No tienes acceso a Oferta disponible' : 'No se pudieron cargar los datos' }}</h2>
                <p>{{ error === 403 ? 'Pídele a un super admin que te dé acceso desde Permisos.' : 'Revisa tu conexión e inténtalo de nuevo.' }}</p>
                <button v-if="error !== 403" type="button" class="od-btn" @click="cargar(false)">Reintentar</button>
            </div>
            <div v-else-if="!listo" class="od-estado" aria-busy="true">
                <HyFLoader variant="inline" />
            </div>
            <div
                v-else
                ref="app"
                aria-live="off"
                @click="onClick"
                @keydown="onKeydown"
                @change="onChange"
                @input="onInput"
                v-html="html"
            ></div>

            <p v-if="listo" class="od-pie">Panel de administración · Inventario · <strong>Oferta disponible</strong>. Datos de la base de House &amp; Flats; precios mensuales convertidos a euros para comparar países.</p>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import { crearMotor, BASE } from './odMotor';
import { crearVistas } from './odVistas';
import { crearPais } from './odPais';

const lista = (v) => (v ? String(v).split(',').map(Number).filter((n) => !isNaN(n)) : []);

export default {
    name: 'OfertaDisponible',
    components: { HyFLoader },
    data() {
        return {
            listo: false,
            error: null,
            actualizando: false,
            falloActualizar: false,
            generado: null,
            html: '',
            migas: '',
        };
    },
    computed: {
        generadoTxt() {
            if (!this.generado) return '';
            return new Date(this.generado).toLocaleString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        },
    },
    watch: {
        $route() {
            if (!this.listo) return;
            const o = this._opts || {};
            this._opts = null;
            this.S = this.leer();
            this.render(o);
        },
    },
    created() {
        // Motor, vistas y estado fuera de data(): son decenas de miles de
        // filas y no tienen que ser reactivas.
        this.O = null;
        this.V = null;
        this.P = null;
        this.S = null;
        this._opts = null;
        this._ruta = '';
        this._espera = null;
        this.cargar(false);
    },
    beforeDestroy() {
        clearTimeout(this._espera);
        document.title = this._titulo || document.title;
    },
    methods: {
        async cargar(refrescar) {
            if (refrescar) { this.actualizando = true; this.falloActualizar = false; }
            else if (!this.listo) this.error = null;
            try {
                const { data } = await axios.get(`${process.env.VUE_APP_API_URL}admin/oferta-disponible`, { params: refrescar ? { refrescar: 1 } : {} });
                this.O = crearMotor(data);
                this.V = crearVistas(this.O);
                this.P = crearPais(this.O, this.V);
                this.generado = data.generado;
                this.S = this.leer();
                if (!this._titulo) this._titulo = document.title;
                this.listo = true;
                this.error = null;
                this.render({ mantener: refrescar });
            } catch (e) {
                const st = e && e.response ? e.response.status : 0;
                if (!this.listo) this.error = st === 403 ? 403 : 'red';
                else this.falloActualizar = true;
            } finally {
                this.actualizando = false;
            }
        },

        // ── Estado ⇄ URL ─────────────────────────────────────────────────
        leer() {
            const { O, P } = this;
            const D = O.D;
            const p = this.$route.query;
            const nivel = this.$route.params.nivel;
            const clave = this.$route.params.clave || '';
            const mesP = p.m;
            const mes = mesP === 't' ? -1 : Math.min(D.meses.length - 1, Math.max(0, parseInt(mesP || '0', 10) || 0));
            const S = { vista: 'inicio', mes };
            if (nivel === 'c' && O.CONT[clave]) { S.vista = 'cont'; S.cont = clave; }
            if (nivel === 'p' && D.paises[clave.toUpperCase()]) {
                S.vista = 'pais';
                S.F = {
                    pais: clave.toUpperCase(), mes,
                    ciudad: p.ci !== undefined ? parseInt(p.ci, 10) : -1,
                    cat: lista(p.cat), tramo: lista(p.tr), grupo: lista(p.gr), est: lista(p.es).filter((i) => i >= 0 && i < O.ESTADIA.length),
                    q: String(p.q || '').toLowerCase().trim(), orden: p.o || 'entrada', tab: p.tab || 'aloj',
                    n: Math.max(P.POR_PAGINA, parseInt(p.n || '0', 10) || 0),
                };
                if (!(S.F.ciudad >= 0 && D.ciudades[S.F.ciudad] && D.ciudades[S.F.ciudad][2] === S.F.pais)) S.F.ciudad = -1;
            }
            return S;
        },
        rutaDe(S) {
            const q = {};
            if (S.mes !== 0) q.m = S.mes < 0 ? 't' : String(S.mes);
            let path = BASE;
            if (S.vista === 'cont') path += '/c/' + S.cont;
            if (S.vista === 'pais') {
                const F = S.F;
                path += '/p/' + F.pais;
                if (F.ciudad >= 0) q.ci = String(F.ciudad);
                if (F.cat.length) q.cat = F.cat.join(',');
                if (F.tramo.length) q.tr = F.tramo.join(',');
                if (F.est.length) q.es = F.est.join(',');
                if (F.grupo.length) q.gr = F.grupo.join(',');
                if (F.q) q.q = F.q;
                if (F.orden !== 'entrada') q.o = F.orden;
                if (F.tab !== 'aloj') q.tab = F.tab;
                if (F.n > this.P.POR_PAGINA) q.n = String(F.n);
            }
            return { path, query: q };
        },
        mismaRuta(r) {
            const a = this.$route;
            if (a.path.replace(/\/$/, '') !== r.path) return false;
            const ka = Object.keys(a.query);
            const kb = Object.keys(r.query);
            return ka.length === kb.length && kb.every((k) => String(a.query[k]) === r.query[k]);
        },
        /** Cambia el estado: reemplaza la URL (filtros) o agrega historial (navegación). */
        cambiar(fn, opciones) {
            const o = opciones || {};
            fn(this.S);
            if (this.S.F) this.S.F.mes = this.S.mes;
            const r = this.rutaDe(this.S);
            if (this.mismaRuta(r)) { this.render(o); return; }
            this._opts = o;
            (o.historial ? this.$router.push(r) : this.$router.replace(r)).catch(() => {});
        },
        ir(href) {
            this.$router.push(href).catch(() => {});
        },

        // ── Render ───────────────────────────────────────────────────────
        pintarMigas() {
            const { O, V, S } = this;
            const m = S.mes !== 0 ? '?m=' + (S.mes < 0 ? 't' : S.mes) : '';
            const l = ['<a href="/admin">Admin</a>', '<span class="od-sep">›</span>', '<span>Inventario</span>', '<span class="od-sep">›</span>'];
            const cont = S.vista === 'cont' ? S.cont : S.vista === 'pais' ? O.contDe(S.F.pais) : null;
            if (!cont) l.push('<span aria-current="page">Oferta disponible</span>');
            else {
                l.push(`<a href="${BASE}${m}">Oferta disponible</a>`, '<span class="od-sep">›</span>');
                if (S.vista === 'cont') l.push(`<span aria-current="page">${O.CONT[cont].n}</span>`);
                else {
                    if (O.CONT[cont]) l.push(`<a href="${BASE}/c/${cont}${m}">${O.CONT[cont].n}</a>`, '<span class="od-sep">›</span>');
                    l.push(`<span aria-current="page">${O.esc(V.nombrePais(S.F.pais))}</span>`);
                }
            }
            this.migas = l.join('');
        },
        render(o) {
            const opts = o || {};
            const { O, V, P, S } = this;
            const ruta = this.$route.path.replace(/\/$/, '');
            const misma = ruta === this._ruta;
            const y = window.scrollY;
            const activo = document.activeElement;
            const foco = activo && activo.id === 'od-q';
            if (S.vista === 'cont') this.html = V.continente(S.cont, S);
            else if (S.vista === 'pais') this.html = P.pais(S.F);
            else this.html = V.inicio(S);
            this.pintarMigas();
            document.title = (S.vista === 'pais' ? V.nombrePais(S.F.pais) + ' · ' : S.vista === 'cont' ? O.CONT[S.cont].n + ' · ' : '') + 'Oferta disponible · Admin House & Flats';
            this._ruta = ruta;
            this.$nextTick(() => {
                const app = this.$refs.app;
                if (!app) return;
                if (misma || opts.mantener) {
                    window.scrollTo(0, y);
                    if (opts.irA) { const el = document.getElementById(opts.irA); if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
                    if (foco) { const q = document.getElementById('od-q'); if (q) { q.focus(); q.setSelectionRange(q.value.length, q.value.length); } }
                    if (opts.enfocar) { const el = app.querySelector(opts.enfocar); if (el) el.focus({ preventScroll: true }); }
                    // Sin animación de entrada al repintar por filtros
                    app.querySelectorAll('.od-card, .od-kpi, .od-hero').forEach((el) => { el.style.animation = 'none'; });
                } else {
                    window.scrollTo(0, 0);
                    if (this.$refs.main) this.$refs.main.focus({ preventScroll: true });
                }
            });
        },

        // ── Eventos (delegación) ─────────────────────────────────────────
        selector(el) {
            const attrs = ['data-mes', 'data-f', 'data-v', 'data-ciudad', 'data-tab', 'data-quitar'];
            return attrs.filter((a) => el.hasAttribute(a)).map((a) => `[${a}="${el.getAttribute(a)}"]`).join('');
        },
        /** Links internos del admin: navegación del router (Ctrl/Mayús abren otra pestaña). */
        onLink(e) {
            const a = e.target.closest('a[href]');
            if (!a) return false;
            const href = a.getAttribute('href');
            if (href.indexOf('/admin') === 0 && !a.target && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                e.preventDefault();
                this.ir(href);
            }
            return true;
        },
        onClick(e) {
            const { P } = this;
            if (this.onLink(e)) return;
            const t = e.target.closest('button, tr[data-ir]');
            if (!t || !this.$refs.app.contains(t)) return;
            const enfocar = t.tagName === 'BUTTON' ? this.selector(t) : null;
            if (t.hasAttribute('data-ir')) { this.ir(t.getAttribute('data-ir')); return; }
            if (t.hasAttribute('data-mes')) { const m = +t.getAttribute('data-mes'); this.cambiar((s) => { s.mes = m; if (s.F) s.F.n = P.POR_PAGINA; }, { enfocar }); return; }
            if (t.hasAttribute('data-exportar')) { this.exportar(t.getAttribute('data-exportar')); return; }
            if (!this.S.F) return;
            if (t.hasAttribute('data-f')) {
                const k = t.getAttribute('data-f');
                const v = +t.getAttribute('data-v');
                this.cambiar((s) => { const l = s.F[k]; const i = l.indexOf(v); if (i >= 0) l.splice(i, 1); else l.push(v); l.sort(); s.F.n = P.POR_PAGINA; }, { enfocar });
            } else if (t.hasAttribute('data-ciudad')) {
                const ci = +t.getAttribute('data-ciudad');
                this.cambiar((s) => { s.F.ciudad = s.F.ciudad === ci ? -1 : ci; s.F.n = P.POR_PAGINA; }, { enfocar });
            } else if (t.hasAttribute('data-tab')) {
                const k = t.getAttribute('data-tab');
                this.cambiar((s) => { s.F.tab = k; }, { irA: t.classList.contains('od-tab') ? null : 'od-resultados', enfocar: `.od-tab[data-tab="${k}"]` });
            } else if (t.hasAttribute('data-quitar')) {
                const k = t.getAttribute('data-quitar');
                this.cambiar((s) => {
                    const F = s.F;
                    if (k === 'todo') { F.ciudad = -1; F.cat = []; F.tramo = []; F.grupo = []; F.est = []; F.q = ''; s.mes = 0; }
                    else if (k === 'mes') s.mes = -1;
                    else if (k === 'ciudad') F.ciudad = -1;
                    else if (k === 'q') F.q = '';
                    else { const [g, v] = k.split(':'); F[g] = F[g].filter((x) => x !== +v); }
                    F.n = P.POR_PAGINA;
                }, { enfocar: '.od-tab[aria-selected="true"]' });
            } else if (t.hasAttribute('data-mas')) {
                this.cambiar((s) => { s.F.n += P.POR_PAGINA; }, { mantener: true });
            }
        },
        onKeydown(e) {
            const tr = e.target.closest('tr[data-ir]');
            if (tr && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); this.ir(tr.getAttribute('data-ir')); }
        },
        onChange(e) {
            const t = e.target;
            if (t.hasAttribute('data-orden')) this.cambiar((s) => { s.F.orden = t.value; }, { enfocar: '#od-orden' });
            if (t.hasAttribute('data-ciudad-sel') && t.value !== '') this.cambiar((s) => { s.F.ciudad = +t.value; s.F.n = this.P.POR_PAGINA; }, { enfocar: '#od-ciudad-mas' });
        },
        onInput(e) {
            if (!e.target.hasAttribute('data-q')) return;
            const v = e.target.value.toLowerCase().trim();
            clearTimeout(this._espera);
            this._espera = setTimeout(() => this.cambiar((s) => { s.F.q = v; s.F.n = this.P.POR_PAGINA; }), 280);
        },

        // ── Exportar (CSV que abre Excel: ; y BOM) ───────────────────────
        exportar(cual) {
            const { O, V, P, S } = this;
            const D = O.D;
            let filas;
            let nombre;
            if (cual === 'lista' && S.F) { filas = P.ordenar(O.filtrar(S.F), S.F); nombre = 'oferta-' + S.F.pais.toLowerCase(); }
            else if (cual === 'cont') { filas = O.filtrar({ cont: S.cont, mes: S.mes, ciudad: -1 }); nombre = 'oferta-' + S.cont; }
            else { filas = O.filtrar({ mes: S.mes, ciudad: -1 }); nombre = 'oferta-total'; }
            if (S.mes >= 0) nombre += '-' + D.meses[S.mes].k;
            const c = (v) => { const t = v === null || v === undefined ? '' : String(v); return /[;"\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
            const cab = ['ID', 'Nombre', 'Dirección', 'Continente', 'País', 'Ciudad', 'Tipo', 'Categoría', 'Huéspedes', 'Disponibilidad', 'Última sincronización', 'Precio €/mes' + (S.mes >= 0 ? ' (' + D.meses[S.mes].nombre + ')' : ''), 'Moneda', 'Precio original', 'Días libres seguidos desde la entrada']
                .concat(D.meses.map((m) => 'Entrada ' + m.nombre)).concat(['Anfitrión', 'Ficha']);
            const lineas = [cab.map(c).join(';')];
            const ficha = window.location.origin + P.FICHA;
            filas.forEach((u) => {
                lineas.push([u.id, u.nombre, u.dir, O.CONT[u.cont] ? O.CONT[u.cont].n : 'Otro', V.nombrePais(u.pais), D.ciudades[u.ci][1], u.cat === 2 ? O.SUB[u.sub] : O.CAT[u.cat].corto, u.categoria, u.cap || '', D.integraciones[u.integ], u.sync, O.precioMes(u, S.mes), u.moneda, u.orig, O.libreDe(u, S.mes) >= D.topeLibre ? '365+' : O.libreDe(u, S.mes)]
                    .concat(u.ent.map((d, m) => (d > 0 ? D.meses[m].k + '-' + String(d).padStart(2, '0') : 'No')))
                    .concat([D.hosts[u.host][1], ficha + u.id]).map(c).join(';'));
            });
            const blob = new Blob(['\ufeff' + lineas.join('\r\n')], { type: 'text/csv;charset=utf-8' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = nombre + '.csv';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
        },
    },
};
</script>

<style src="./ofertaDisponible.css"></style>
